import {
  CommandPaletteBackdrop,
  CommandPaletteFooter,
  CommandPaletteInput,
  CommandPaletteItem,
  CommandPaletteItemMeta,
  CommandPaletteList,
  CommandPaletteSearchInput,
  CommandPaletteSection,
  CommandPaletteShell,
  Kbd,
  Text,
} from '@zrsystem/ui';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/providers/auth-provider.js';
import { useCommandPaletteContext } from '@/app/surfaces/command-palette-provider.js';
import {
  filterCommandPaletteItems,
  getCommandPaletteItems,
} from '@/app/surfaces/command-palette-registry.js';
import type { CommandPaletteItem as CommandPaletteEntry } from '@/app/surfaces/command-palette-types.js';
import { useQuickPanelContext } from '@/app/surfaces/quick-panel-provider.js';

function groupItemsBySection(items: CommandPaletteEntry[]): Map<string, CommandPaletteEntry[]> {
  const groups = new Map<string, CommandPaletteEntry[]>();

  for (const item of items) {
    const sectionItems = groups.get(item.section) ?? [];
    sectionItems.push(item);
    groups.set(item.section, sectionItems);
  }

  return groups;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { openQuickPanel } = useQuickPanelContext();
  const { isOpen, query, setQuery, closeCommandPalette } = useCommandPaletteContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const items = useMemo(() => getCommandPaletteItems(), []);
  const filteredItems = useMemo(() => filterCommandPaletteItems(items, query), [items, query]);
  const groupedItems = useMemo(() => groupItemsBySection(filteredItems), [filteredItems]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setActiveIndex(0);
    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!isOpen) {
    return null;
  }

  function runItem(item: CommandPaletteEntry) {
    item.run({
      navigate,
      signOut,
      openQuickPanel,
      closeCommandPalette,
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, Math.max(filteredItems.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const item = filteredItems[activeIndex];
      if (item) {
        runItem(item);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeCommandPalette();
    }
  }

  let itemIndex = -1;

  return (
    <CommandPaletteBackdrop
      onClick={() => {
        closeCommandPalette();
      }}
    >
      <CommandPaletteShell
        aria-label="Paleta de comandos"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <CommandPaletteInput>
          <CommandPaletteSearchInput
            ref={inputRef}
            value={query}
            placeholder="Buscar comando…"
            aria-label="Buscar comando"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onKeyDown={handleKeyDown}
          />
          <Kbd>Esc</Kbd>
        </CommandPaletteInput>

        <CommandPaletteList>
          {filteredItems.length === 0 ? (
            <Text muted as="p" style={{ padding: 'var(--s-3)', display: 'block' }}>
              Nenhum comando encontrado.
            </Text>
          ) : (
            [...groupedItems.entries()].map(([section, sectionItems]) => (
              <CommandPaletteSection key={section}>
                <Text
                  variant="caps"
                  subtle
                  as="div"
                  style={{ padding: 'var(--s-2) var(--s-2) var(--s-1)' }}
                >
                  {section}
                </Text>
                {sectionItems.map((item) => {
                  itemIndex += 1;
                  const isActive = itemIndex === activeIndex;

                  return (
                    <CommandPaletteItem
                      key={item.id}
                      active={isActive}
                      onMouseEnter={() => {
                        setActiveIndex(itemIndex);
                      }}
                      onClick={() => {
                        runItem(item);
                      }}
                    >
                      {item.label}
                      {item.shortcutLabel ? (
                        <CommandPaletteItemMeta>{item.shortcutLabel}</CommandPaletteItemMeta>
                      ) : null}
                    </CommandPaletteItem>
                  );
                })}
              </CommandPaletteSection>
            ))
          )}
        </CommandPaletteList>

        <CommandPaletteFooter>
          <Text variant="xs" muted as="span">
            ↑↓ navegar · Enter selecionar · Esc fechar
          </Text>
        </CommandPaletteFooter>
      </CommandPaletteShell>
    </CommandPaletteBackdrop>
  );
}
